import React from "react";

import { bdtFmt, DetailRow, formatShareQty } from "./InvestorTxnDetailUi";
import TransferProgressSteps from "./TransferProgressSteps";
import {
  canCancelOutgoingTransfer,
  formatMmSs,
  msUntilOutgoingTransferCancel,
} from "../../utils/secondaryTransferCancel";
import { transferStatusLabel, transferStatusTone } from "../../utils/transferLabels";

function rowGrossBdt(row) {
  return Number(row?.transfer_gross_bdt || row?.total_bdt || 0);
}

function rowFeeBdt(row) {
  return Number(row?.fee_bdt || 0);
}

function rowNetBdt(row) {
  if (row?.net_cash_bdt != null) return Number(row.net_cash_bdt);
  const g = rowGrossBdt(row);
  const f = rowFeeBdt(row);
  return g > 0 ? g - f : 0;
}

export default function TransferOfferDetailModal({ row, onClose, onRequestCancel, cancelling }) {
  if (!row) return null;

  const status = String(row.status || "").toUpperCase();
  const isPending = status === "PENDING";
  const waitMs = msUntilOutgoingTransferCancel(row.requested_at);
  const cancelReady = canCancelOutgoingTransfer(row);
  const gross = rowGrossBdt(row);
  const fee = rowFeeBdt(row);
  const net = rowNetBdt(row);
  const settledShares = row.shares_approved != null ? formatShareQty(row.shares_approved) : null;
  const recipient = row.transfer_recipient_display || "—";
  const rejectNote = status === "REJECTED" && row.admin_notes ? String(row.admin_notes).trim() : "";

  return (
    <div className="invx-about-modal-overlay" onClick={onClose} role="presentation">
      <div
        className="invx-about-modal investor-offer-detail"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="transfer-detail-title"
        aria-modal="true"
      >
        <button type="button" className="invx-about-modal-close" onClick={onClose} aria-label="Close">
          ×
        </button>

        <header className="investor-offer-detail__header">
          <div className="investor-offer-detail__header-main">
            <p className="investor-offer-detail__eyebrow">Transfer offer</p>
            <h3 id="transfer-detail-title" className="investor-offer-detail__title">
              {row.transfer_ref || "Transfer"}
            </h3>
            <p className="investor-offer-detail__project">{row.project_display || "—"}</p>
            <p className="investor-offer-detail__recipient-line">
              To <strong>{recipient}</strong>
            </p>
          </div>
          <span className={`badge ${transferStatusTone(row.status)}`}>{transferStatusLabel(row.status)}</span>
        </header>

        <div className="investor-offer-detail__progress">
          <TransferProgressSteps status={row.status} sentAt={row.requested_at} settledAt={row.processed_at} />
        </div>

        <div className="investor-offer-detail__panels">
          <section className="investor-offer-detail__panel" aria-labelledby="transfer-detail-offer">
            <h4 id="transfer-detail-offer" className="investor-offer-detail__panel-title">
              Offer
            </h4>
            <DetailRow label="Shares offered">{formatShareQty(row.shares)}</DetailRow>
            <DetailRow label="Shares settled" empty={!settledShares}>
              {settledShares}
            </DetailRow>
            <DetailRow label="Locked NAV / share">{bdtFmt.format(Number(row.price_per_share || 0))}</DetailRow>
          </section>

          <section className="investor-offer-detail__panel" aria-labelledby="transfer-detail-amounts">
            <h4 id="transfer-detail-amounts" className="investor-offer-detail__panel-title">
              Amounts
            </h4>
            <DetailRow label="Gross (est.)" empty={gross <= 0}>
              {gross > 0 ? bdtFmt.format(gross) : null}
            </DetailRow>
            <DetailRow label="Transfer fee" empty={fee <= 0}>
              {fee > 0 ? bdtFmt.format(fee) : null}
            </DetailRow>
            <DetailRow label="Net to recipient" empty={net <= 0}>
              {net > 0 ? bdtFmt.format(net) : null}
            </DetailRow>
          </section>
        </div>

        {rejectNote ? (
          <p className="investor-offer-detail__hint muted">
            <strong>Recipient declined:</strong> {rejectNote}
          </p>
        ) : null}

        {isPending ? (
          <p className="investor-offer-detail__hint muted">
            {cancelReady
              ? "You can withdraw this offer if it has not been accepted yet."
              : `Cancel becomes available in ${formatMmSs(waitMs)} (5 minutes after sending).`}
          </p>
        ) : null}

        <footer className="investor-offer-detail__footer">
          <button type="button" className="btn-secondary compact-btn" onClick={onClose}>
            Close
          </button>
          {isPending && onRequestCancel ? (
            <button
              type="button"
              className="btn-danger compact-btn"
              disabled={cancelling || !cancelReady}
              title={cancelReady ? "Withdraw this offer" : `Cancel in ${formatMmSs(waitMs)}`}
              onClick={() => onRequestCancel(row)}
            >
              {cancelling ? "Cancelling…" : "Cancel offer"}
            </button>
          ) : null}
        </footer>
      </div>
    </div>
  );
}
